import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CTA, { CTAProps } from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { CardReaderDisplayMessage } from 'Src/feature/card-reader/CardReaderDisplayMessage';
import { CardReaderPaymentMessage } from 'Src/feature/card-reader/CardReaderPaymentMessage';
import { Check } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useCheckDescribe } from 'Src/feature/check-util/useCheckDescribe';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalSubTitle } from 'Src/feature/modal/ModalSubTitle';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useCheckPagePayment } from 'Src/feature/payment/useCheckPagePayment';
import { useCheckPagePaymentPermissions } from 'Src/feature/payment/useCheckPagePaymentPermissions';
import { usePaymentFlow } from 'Src/feature/payment/usePaymentFlow';
import useRoute from 'Src/routes/useRoute';

const TEST_ID = {
  root: `ExecuteModal`,
  cta: {
    cancel: `ExecuteModal-cancel`,
    cash: `ExecuteModal-cash`,
    giftCardLookup: `ExecuteModal-giftCardLookup`,
    manualCC: `ExecuteModal-manualCC`,
    other: `ExecuteModal-other`,
    preAuth: `ExecuteModal-preAuth`,
  }
};

interface ExecuteModalProps {
  control: ReturnType<typeof useCheckPagePayment>['modal']['execute'];
}

const CTA_SHARED_PROPS: Partial<CTAProps> = { variant: 'outlined', maxWidth: '100%', minWidth: 'lg' };

const ExecuteModalContent: FC<ExecuteModalProps> = ({ control }) => {
  const { t } = useTranslation();
  const { route } = useRoute();

  const paymentFlow = usePaymentFlow();

  const checkId = route.params.active.checkId ? parseInt(route.params.active.checkId, 10) : undefined;
  const checkGetOne = useCheck.query.getOne(checkId);
  const grantedPermissions = useCheckPagePaymentPermissions();

  const described = useCheckDescribe({ check: checkGetOne.data });

  let amount = 0;
  let title = <></>;
  if (paymentFlow.is.split) {
    title = (
      <>
        <ModalTitle copy={t('payment.labels.splitCheckEvenlyCount', { count: paymentFlow.split.count, totalParts: paymentFlow.split.response.length })} />
        <ModalSubTitle copy={t('payment.labels.payForPortion')} />
      </>
    );
    amount = paymentFlow.split.amount ?? 0;
  } else if (paymentFlow.is.part) {
    title = <ModalTitle copy={t('payment.labels.partialPayment')} />;
    amount = paymentFlow.part.amount ?? 0;
  } else {
    amount = described.check?.balance.unpaid ?? 0;
  }


  return (
    <>
      <ModalHeader>
        {title}
      </ModalHeader>
      <ModalContent flex={{ alignItems: 'center', justifyContent: 'center', gap: 'sm' }}>
        {(grantedPermissions.ccPayment.isAllowed || grantedPermissions.giftCardPayment.isAllowed) && (
          <>
            <CardReaderPaymentMessage />
            <CardReaderDisplayMessage />
          </>
        )}
        <Copy variant="h6" padding="md" align="center" value={t('labels.util.currency', { value: amount })} />
        {
          (described.payment.startTabWithCard && grantedPermissions.ccPayment.isAllowed) && (
            <CTA
              {...CTA_SHARED_PROPS}
              data-testid={TEST_ID.cta.preAuth}
              onClick={() => control.onPaymentTypeClick({ type: 'preAuth', payment: described.payment.startTabWithCard as Check['payments'][number], amount })}
            >
              <Copy variant="button-label" align="left" fullWidth ellipsis value={t('payment.labels.paymentInfo', { cardBrand: described.payment.startTabWithCard?.ccData?.cardBrand, lastFour: described.payment.startTabWithCard?.ccData?.lastFourCcNumber })} />
              <Copy variant="button-label" align="right" value={t('payment.labels.auth')} />
            </CTA>
          )
        }
        {grantedPermissions.manualPayment.isAllowed && (
          <CTA
            {...CTA_SHARED_PROPS}
            data-testid={TEST_ID.cta.manualCC}
            label={t('labels.manualCC')}
            onClick={() => control.onPaymentTypeClick({ type: 'manual', amount })}
          />
        )}
        {grantedPermissions.giftCardPayment.isAllowed && (
          <CTA
            {...CTA_SHARED_PROPS}
            data-testid={TEST_ID.cta.giftCardLookup}
            label={t('labels.giftCardLookup')}
            onClick={() => control.onPaymentTypeClick({ type: 'gc' })}
          />
        )}
        {grantedPermissions.cashPayment.isAllowed && (
          <CTA
            {...CTA_SHARED_PROPS}
            data-testid={TEST_ID.cta.cash}
            label={t('labels.cash')}
            onClick={() => control.onPaymentTypeClick({ type: 'cash' })}
          />
        )}
        {grantedPermissions.otherPayment.isAllowed && (
          <CTA
            {...CTA_SHARED_PROPS}
            data-testid={TEST_ID.cta.other}
            label={t('labels.other')}
            onClick={() => control.onPaymentTypeClick({ type: 'other' })}
          />
        )}
      </ModalContent>
      <ModalActions>
        <ModalAction
          data-testid={TEST_ID.cta.cancel}
          label={t('labels.cancel')}
          color="secondary"
          onClick={() => control.close('on-user-cancel')}
        />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const ExecuteModal: FC<ExecuteModalProps> = ({ control }) => (
  <Modal
    data-testid={TEST_ID.root}
    open={control.isOpen}
    onBackDropClick={() => control.close('on-user-cancel')}
  >
    <ExecuteModalContent control={control} />
  </Modal>
);

export { ExecuteModal, TEST_ID };
